<template>
  <modal-wrapper
    title="Generar QR"
    :isVisible="modelValue"
    :showSaveButton="false"
    @cerrar-modal="cerrarModal"
  >
    <mensaje-invalidos v-if="v$.$error" class="m-0 my-3" />
    <div class="formgrid grid my-3">
      <div class="col-12">
        <p-float-label label="Descripción" required>
          <input-text
            v-model="v$.Descripcion.$model"
            :class="{ 'p-invalid': v$.Descripcion.$error }"
          />
        </p-float-label>
        <small v-if="v$.Descripcion.$error" class="p-error mt-1">
          {{ v$.Descripcion.$errors[0].$message }}
        </small>
      </div>
      <div class="col-12 mt-5">
        <p-float-label label="Cantidad" required>
          <input-number
            v-model="v$.Cantidad.$model"
            :useGrouping="false"
            :min="1"
            :max="10000"
            :class="{ 'p-invalid': v$.Cantidad.$error }"
          />
        </p-float-label>
        <small v-if="v$.Cantidad.$error" class="p-error mt-1">
          {{ v$.Cantidad.$errors[0].$message }}
        </small>
      </div>
      <div class="col-12 mt-5 flex justify-content-end">
        <button-element
          label="Generar QR"
          icon="pi pi-qrcode"
          @click="generarQrHandler"
        />
      </div>
    </div>
  </modal-wrapper>
</template>

<script setup>
import { reactive, ref } from "vue";
import { useStore } from "vuex";
import { useToast } from "primevue/usetoast";
import { useVuelidate } from "@vuelidate/core";
import {
  helpers,
  maxLength,
  maxValue,
  minValue,
  required,
} from "@vuelidate/validators";
import { alertStatus } from "@/helpers/alertStatus";
import * as alerts from "@/helpers/alertMessages";
import { generarQr } from "../services/soporte-cmms";

defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue"]);

const store = useStore();
const toast = useToast();

const form = reactive({
  Descripcion: "",
  Cantidad: null,
});

const erroresServidor = ref({});

const reglas = {
  Descripcion: {
    required: helpers.withMessage("La descripción es obligatoria.", required),
    maxLength: helpers.withMessage(
      "La descripción no puede superar 255 caracteres.",
      maxLength(255)
    ),
  },
  Cantidad: {
    required: helpers.withMessage("La cantidad es obligatoria.", required),
    minValue: helpers.withMessage(
      "La cantidad debe ser al menos 1.",
      minValue(1)
    ),
    maxValue: helpers.withMessage(
      "La cantidad no puede superar 10000.",
      maxValue(10000)
    ),
  },
};

const v$ = useVuelidate(reglas, form, { $externalResults: erroresServidor });

const resetModal = () => {
  form.Descripcion = "";
  form.Cantidad = null;
  erroresServidor.value = {};
  v$.value.$reset();
  emit("update:modelValue", false);
};

const cerrarModal = async () => {
  if (v$.value.$anyDirty) {
    const confirmacion = await alerts.descartarCambios();
    if (!confirmacion?.isConfirmed) return;
  }

  resetModal();
};

const descargarExcel = (blob, nombre) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", nombre);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const generarQrHandler = async () => {
  erroresServidor.value = {};

  const esValido = await v$.value.$validate();
  if (!esValido) return;

  store.commit("setIsLoading", true);
  const { data, status } = await generarQr(form);
  store.commit("setIsLoading", false);

  if (status === 200) {
    descargarExcel(data, `QR-${form.Descripcion}.xlsx`);
    toast.add(alerts.archivoGenerado);
    resetModal();
    return;
  }

  if (status === 422 && data?.errors) {
    erroresServidor.value = data.errors;
    return;
  }

  alertStatus(status, data);
};
</script>
